/**
 * B521 — the product page's derivations (Figma 364-2), pure.
 *
 * - `calendarMonth`: the month the selected day lives in, as MonthCalendar
 *   cells — every day of the month, pressable where a departure is on
 *   sale, with the month doors pointing at the nearest month that has one.
 * - The price under a day: the price lists a storefront can read today are
 *   FLAT (one price per ticket type for the whole list), so exactly one
 *   number is honest — the lead ticket's price, under the SELECTED day
 *   only. Day-varying prices need a read that carries day rules; until one
 *   exists no cell invents a number (B521's "never a fabricated per-day
 *   price").
 * - `pagePhotos` / `pageDescription`: the product read (catalog.get) wins;
 *   until it answers — or if it refuses — the card's own photo and first
 *   sentence stand in, so the page never flashes emptier than the card.
 */
import type { CalendarDay, CarouselPhoto } from "@transact/ui";
import type { DayGroup } from "../ui/departures.js";
import type { ProductSummary } from "../ui/screens.js";
import { formatMoney } from "./money.js";

export interface CalendarMonthFace {
  readonly title: string;
  readonly leadingBlanks: number;
  readonly days: readonly CalendarDay[];
  /** The first on-sale day of the nearest earlier/later month; absent = no door. */
  readonly prevKey?: string;
  readonly nextKey?: string;
  readonly legend?: string;
}

const monthOf = (key: string): string => key.slice(0, 7);

/** The lead ticket's price — the first ticket type the channel prices. */
export function leadPrice(card: ProductSummary): string | undefined {
  const priced = card.variants.find((v) => v.displayPrice !== undefined);
  return priced?.displayPrice === undefined ? undefined : formatMoney(priced.displayPrice);
}

export function calendarMonth(
  card: ProductSummary,
  groups: readonly DayGroup[],
  selectedKey: string | undefined,
  locale?: string,
): CalendarMonthFace | undefined {
  const selected = groups.find((g) => g.key === selectedKey) ?? groups[0];
  if (selected === undefined) return undefined;
  const month = monthOf(selected.key);
  const [y, m] = month.split("-").map((n) => Number.parseInt(n, 10)) as [number, number];
  const utc = { timeZone: "UTC" } as const;
  const title = new Intl.DateTimeFormat(locale, { ...utc, month: "long", year: "numeric" }).format(Date.UTC(y, m - 1, 1));
  const labelFmt = new Intl.DateTimeFormat(locale, { ...utc, weekday: "long", month: "long", day: "numeric" });
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const byKey = new Map(groups.map((g) => [g.key, g]));
  const price = leadPrice(card);
  const days: CalendarDay[] = Array.from({ length: daysInMonth }, (_, i) => {
    const d = i + 1;
    const key = `${month}-${String(d).padStart(2, "0")}`;
    const group = byKey.get(key);
    return {
      key,
      dayOfMonth: d,
      label: group?.label ?? labelFmt.format(Date.UTC(y, m - 1, d)),
      available: group !== undefined,
      ...(group !== undefined && key === selected.key && price !== undefined ? { price } : {}),
    };
  });
  const months = [...new Set(groups.map((g) => monthOf(g.key)))];
  const at = months.indexOf(month);
  const firstOf = (mm: string | undefined): string | undefined =>
    mm === undefined ? undefined : groups.find((g) => monthOf(g.key) === mm)?.key;
  const prevKey = firstOf(at > 0 ? months[at - 1] : undefined);
  const nextKey = firstOf(at >= 0 && at < months.length - 1 ? months[at + 1] : undefined);
  return {
    title,
    leadingBlanks: new Date(Date.UTC(y, m - 1, 1)).getUTCDay(),
    days,
    ...(prevKey !== undefined ? { prevKey } : {}),
    ...(nextKey !== undefined ? { nextKey } : {}),
    ...(price !== undefined ? { legend: "The same price every day — shown under the day you pick." } : {}),
  };
}

export interface ProductMedia {
  readonly photos?: readonly CarouselPhoto[];
  readonly description?: string;
}

export function pagePhotos(card: ProductSummary, media: ProductMedia | undefined): readonly CarouselPhoto[] {
  return media?.photos ?? card.presentation?.photos ?? [];
}

export function pageDescription(card: ProductSummary, media: ProductMedia | undefined): string | undefined {
  return media?.description ?? card.presentation?.summary;
}

/** "Tue 22 Sep" — the picks line's day, in the display zone. */
export function shortDayWords(ms: number, opts: { readonly locale?: string; readonly timeZone?: string }): string {
  const fmt = new Intl.DateTimeFormat(opts.locale, {
    ...(opts.timeZone !== undefined ? { timeZone: opts.timeZone } : {}),
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const parts = Object.fromEntries(fmt.formatToParts(ms).map((p) => [p.type, p.value]));
  return `${parts["weekday"] ?? ""} ${parts["day"] ?? ""} ${parts["month"] ?? ""}`.trim();
}

/**
 * catalog.get's answer → the page's media. Tolerant by construction (the
 * storefront reads additive answers): anything not shaped like photos or a
 * description is ignored, and an empty answer is no media at all.
 */
export function mediaFromProductRead(res: unknown): ProductMedia | undefined {
  const pr = (res as { product?: { presentation?: { photos?: unknown; description?: unknown } } } | undefined)?.product?.presentation;
  if (pr === undefined) return undefined;
  const photos = Array.isArray(pr.photos)
    ? (pr.photos as unknown[]).filter(
        (p): p is CarouselPhoto => typeof p === "object" && p !== null && typeof (p as { url?: unknown }).url === "string",
      )
    : undefined;
  const description = typeof pr.description === "string" && pr.description.trim() !== "" ? pr.description : undefined;
  if ((photos === undefined || photos.length === 0) && description === undefined) return undefined;
  return {
    ...(photos !== undefined && photos.length > 0 ? { photos } : {}),
    ...(description !== undefined ? { description } : {}),
  };
}
